'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { apiGetTopCategories } from '@/apis/page_optimization'
import { Cat, Dog, Fish, Bird, Rabbit, Heart, Bone, Scissors, Droplets, PawPrint, ArrowRight, ChevronRight } from 'lucide-react'

const C = { PRIMARY: '#F8F246', SECONDARY: '#8C5826', YELLOW: '#FF9934', BROWN: '#8C5826', LIGHT: '#fffbf0' }

interface Props { companyId: string; slug_type: string; cat_slug: string }

const petIcons: Record<string, any> = {
    dog: Dog, cat: Cat, fish: Fish, bird: Bird,
    small: Rabbit, food: Bone, health: Heart,
    grooming: Scissors, supplies: Droplets,
}

const palettes = [
    { bg: '#FFF4E0', icon: '#FF9934', border: '#FFD580' },
    { bg: '#E0F7F9', icon: '#2CBBC3', border: '#A8E6EA' },
    { bg: '#FFF9D6', icon: '#D4A800', border: '#F8F246' },
    { bg: '#F3EDE8', icon: '#8C5826', border: '#D4A87A' },
    { bg: '#E8F5E9', icon: '#43A047', border: '#A5D6A7' },
    { bg: '#FCE4EC', icon: '#E91E63', border: '#F48FB1' },
    { bg: '#EDE7F6', icon: '#7E57C2', border: '#CE93D8' },
    { bg: '#E3F2FD', icon: '#1E88E5', border: '#90CAF9' },
]

const PetCareCategories = ({ companyId, slug_type, cat_slug }: Props) => {
    const [categories, setCategories] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        apiGetTopCategories(companyId)
            .then(r => r?.data?.categories && setCategories(r.data.categories.slice(0, 8)))
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [companyId])

    const getIcon = (name: string) => {
        const n = name.toLowerCase()
        for (const [key, icon] of Object.entries(petIcons)) {
            if (n.includes(key)) return icon
        }
        return PawPrint
    }

    if (loading) return (
        <div className="py-14 px-4">
            <div className="max-w-7xl mx-auto">
                <div className="h-8 bg-gray-200 rounded w-48 mb-8 mx-auto animate-pulse" />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />)}
                </div>
            </div>
        </div>
    )

    if (!categories.length) return null

    return (
        <section className="py-10 md:py-14 px-4" style={{ background: '#fffaf5' }}>
            <div className="max-w-7xl mx-auto">

                {/* Header */}
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: '#8C5826' }}>
                            Browse by Type
                        </p>
                        <h2 className="text-2xl md:text-3xl font-black text-gray-900">
                            Shop by<span style={{ color: C.PRIMARY }}> Category</span>
                        </h2>
                    </div>
                    <Link href="/category"
                        className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-extrabold text-xs sm:text-sm transition-all duration-300 hover:scale-105 shadow-sm whitespace-nowrap flex-shrink-0"
                        style={{
                            background: '#F8F246',
                            color: '#8C5826',
                            border: '1px solid rgba(140,88,38,0.15)',
                            boxShadow: '0 4px 16px rgba(248, 242, 70, 0.35)',
                        }}>
                        <span>All Categories</span>
                        <ChevronRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>

                {/* Category grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {categories.map((category, i) => {
                        const Icon = getIcon(category.name)
                        const p = palettes[i % palettes.length]
                        const cleanedUrl = category.url?.replace(/^\/?category\//, '') || `category-${category.id}`

                        return (
                            <Link key={category.id} href={`/category/${cleanedUrl}`}
                                className="group flex flex-col items-center gap-3 py-6 px-4 rounded-2xl text-center transition-all duration-250 hover:-translate-y-1.5 hover:shadow-lg"
                                style={{
                                    background: p.bg,
                                    border: `1.5px solid ${p.border}`,
                                }}>

                                {/* Icon */}
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-200 group-hover:scale-110 group-hover:rotate-3"
                                    style={{ background: p.icon + '20' }}>
                                    <Icon size={28} style={{ color: p.icon }} />
                                </div>

                                {/* Name */}
                                <p className="text-sm font-black leading-tight" style={{ color: C.BROWN }}>
                                    {category.name}
                                </p>

                                {/* Deal count */}
                                {(category.total_offers ?? 0) > 0 && (
                                    <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full"
                                        style={{ background: p.icon + '15', color: p.icon }}>
                                        {category.total_offers} deals
                                    </span>
                                )}
                            </Link>
                        )
                    })}
                </div>

                {/* Mobile CTA */}
                <div className="text-center mt-8 sm:hidden">
                    <Link href="/category"
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold"
                        style={{ background: '#8C5826', color: '#fff' }}>
                        All Categories <ArrowRight size={13} />
                    </Link>
                </div>

            </div>
        </section>
    )
}

export default PetCareCategories
